import type { TimelineBlock } from '@/types/timeline';
import styles from './DayTimeline.module.css';

interface DayTimelineProps {
  startHour: number;
  endHour: number;
  blocks: readonly TimelineBlock[];
  showAxis?: boolean;
  showLegend?: boolean;
}

function formatTime(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  const period = h >= 12 ? 'PM' : 'AM';
  const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${displayH}:${String(m).padStart(2, '0')} ${period}`;
}

export function DayTimeline({
  startHour,
  endHour,
  blocks,
  showAxis = true,
  showLegend = false,
}: DayTimelineProps) {
  const totalMin = (endHour - startHour) * 60;
  const startMin = startHour * 60;

  const hours: number[] = [];
  for (let h = startHour; h <= endHour; h++) {
    hours.push(h);
  }

  // Collect unique labels for legend
  const legendEntries = showLegend
    ? Array.from(new Map(blocks.map((b) => [b.label, b.colorToken])).entries())
    : [];

  return (
    <div className={styles.wrapper} data-testid="day-timeline">
      {showAxis && (
        <div className={`${styles.axis} timeline-axis`} data-testid="timeline-axis">
          {hours.map((h) => {
            const pct = ((h - startHour) / (endHour - startHour)) * 100;
            const period = h >= 12 ? 'PM' : 'AM';
            const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
            return (
              <time
                key={h}
                className={styles.tick}
                style={{ left: `${pct}%` }}
                dateTime={String(h).padStart(2, '0') + ':00'}
              >
                {displayH}
                {period}
              </time>
            );
          })}
        </div>
      )}
      <div className={styles.track}>
        {blocks.map((block, i) => {
          const leftPct = ((block.startMin - startMin) / totalMin) * 100;
          const widthPct = ((block.endMin - block.startMin) / totalMin) * 100;
          return (
            <div
              key={`${block.label}-${i}`}
              className={styles.block}
              style={{
                left: `${leftPct}%`,
                width: `${widthPct}%`,
                backgroundColor: `var(${block.colorToken})`,
              }}
              title={`${block.label}: ${formatTime(block.startMin)} \u2013 ${formatTime(block.endMin)}${block.detail ? ` \u2014 ${block.detail}` : ''}`}
              aria-label={`${block.label}: ${formatTime(block.startMin)} to ${formatTime(block.endMin)}`}
            />
          );
        })}
      </div>
      {showLegend && legendEntries.length > 0 && (
        <div className={styles.legend} data-testid="timeline-legend">
          {legendEntries.map(([label, token]) => (
            <div key={label} className={styles.legendItem}>
              <span
                className={styles.legendSwatch}
                style={{ backgroundColor: `var(${token})` }}
                aria-hidden="true"
              />
              <span className={styles.legendLabel}>{label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
